import dayjs from 'dayjs';
import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import axios from '@/axios';
import { t } from '@/i18n/helpers';
import { sortDays, aggregateByDay, normalizeSeries } from '@/utils/chartData';
import type { ApiPayload, DiagramRow } from '@/types/leadsStats';

type LeadsStatsParams = {
    agentList: number;
    range:     [string, string];
    ids?:      number;
};

export const useLeadsStatsStore = defineStore('leads-stats', () => {
    const totalCount = ref(0);
    const targetedCount = ref(0);
    const callCount = ref(0);
    const formCount = ref(0);
    const leadsCount = ref(0);
    const callDiagramList = ref<DiagramRow[]>([]);
    const formDiagramList = ref<DiagramRow[]>([]);
    const agents = ref<Record<string, string> | null>(null);
    const listStaticPlacement = ref<Record<string, boolean>>({});
    const lastKey = ref<string | null>(null);

    const daysGrid = computed(() => {
        const days = new Set<string>();

        for (const row of callDiagramList.value) {
            days.add(dayjs(row.date).format('YYYY-MM-DD'));
        }
        for (const row of formDiagramList.value) {
            days.add(dayjs(row.date).format('YYYY-MM-DD'));
        }

        return sortDays(days);
    });

    const callChartSeries = computed(() => {
        const map = aggregateByDay(callDiagramList.value);

        return normalizeSeries(daysGrid.value, map);
    });
    const formChartSeries = computed(() => {
        const map = aggregateByDay(formDiagramList.value);

        return normalizeSeries(daysGrid.value, map);
    });
    const hasGraph = computed(() => totalCount.value > 0);

    function setFromResponse(response: ApiPayload) {
        totalCount.value = response.totalCount ?? 0;
        targetedCount.value = response.targetedCount ?? 0;
        callCount.value = response.callCount ?? 0;
        formCount.value = response.formCount ?? 0;
        leadsCount.value = response.leadsCount ?? 0;

        callDiagramList.value = Array.isArray(response.callDiagramList) ? response.callDiagramList : [];
        formDiagramList.value = Array.isArray(response.formDiagramList) ? response.formDiagramList : [];

        agents.value = response.agents ?? null;
        listStaticPlacement.value = response.listStaticPlacement ?? {};
    }

    async function fetchStats(params: LeadsStatsParams) {
        const key = JSON.stringify(params);

        if (lastKey.value === key) return;

        lastKey.value = key;

        try {
            const { data } = await axios.get('cabinet/requests/stats', {
                params,
            });

            const payload = data?.data || {};
            setFromResponse(payload);

            return payload;
        } catch {
            throw new Error(t('home.loadError'));
        }
    }

    return {
        agents,
        callChartSeries,
        callCount,
        callDiagramList,
        fetchStats,
        formChartSeries,
        formCount,
        formDiagramList,
        hasGraph,
        leadsCount,
        listStaticPlacement,
        setFromResponse,
        targetedCount,
        totalCount,
    };
});
